import React, { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NavigateFunction } from 'react-router-dom';
import { Card, Typography, Row, Col, Button, Tag, Badge, Statistic, Space, Divider, Timeline } from 'antd';
import { ErgonomicsTour, ErgonomicsTourButton } from './ErgonomicsTour';
import { useErgonomicsTour } from './useErgonomicsTour';
import {
  RocketOutlined,
  CheckCircleOutlined,
  BarChartOutlined,
  ShoppingCartOutlined,
  SafetyCertificateOutlined,
  CloudOutlined,
  EyeOutlined,
  UserOutlined,
  TeamOutlined,
  PieChartOutlined,
  ApartmentOutlined,
  SettingOutlined,
  LockOutlined,
  TableOutlined,
  ApiOutlined,
  DatabaseOutlined,
  TrophyOutlined,
  StarOutlined,
  BugOutlined,
  FileProtectOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { RubleIcon } from '../../components/common';
import styles from '../../styles/pages/DemoPage.module.scss';
import { useAuth } from '../../hooks/useAuth';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { ErgonomicsDemoTable } from './ErgonomicsDemoTable';

const { Title, Paragraph, Text } = Typography;

// ---------------------------------------------------------------------------
// Контент главной — взят из landing plng.pdf без изменения смысла, риторики
// и формулировок. Если правишь текст здесь, обнови и PDF (или наоборот).
// ---------------------------------------------------------------------------

const TRANSPARENCY_BULLETS = [
  'Вся информация по планам продаж и закупок в одном месте',
  'Информация «живая» в режиме реального времени',
  'Не теряется информация при увольнении сотрудников и передаче клиентов от МП к МП',
  'Возможность прозрачной калибровки объемов к продаже и закупке с учетом ТМЗ',
  'Повышается уровень прозрачности качества данных в первоисточниках => видна зависимость => повышение осознания значимости DQ',
  'Гибкая пользовательская настройка параметров: ключевой ассортимент, признаки, корректировка цен, структура отгрузок и пр. (независимость от поддержки)',
  'Контроль за отклонениями на каждом этапе (по объему и структуре)',
];

// ---------------------------------------------------------------------------
// Тело модуля «План продаж» — выделено отдельным компонентом, потому что у
// него развитая структура (поэтапная цепочка + два дополнительных режима).
// Остальные модули укладываются в один абзац — для них inline JSX в LOGIC_MODULES.
// ---------------------------------------------------------------------------

const SALES_TOP_DOWN_STAGES = [
  'План КД по БГ',
  'План ЦПР-ТН-SKU',
  'План ЦПР-ТН-SKU в разрезе БГ',
  'План РБГ по МП в структуре ЦПР-ТН-SKU',
];

const FlowEyebrow: React.FC<{ children: React.ReactNode; tone?: 'primary' | 'muted' }> = ({
  children,
  tone = 'primary',
}) => (
  <div
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 10,
      padding: '4px 10px',
      borderRadius: 999,
      background: tone === 'primary' ? 'rgba(30, 64, 175, 0.08)' : 'rgba(15, 23, 42, 0.04)',
      color: tone === 'primary' ? '#1e40af' : '#475569',
      fontSize: 11,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      fontWeight: 700,
    }}
  >
    {children}
  </div>
);

const FlowItem: React.FC<{
  eyebrow: React.ReactNode;
  tone?: 'primary' | 'muted';
  children: React.ReactNode;
}> = ({ eyebrow, tone = 'primary', children }) => (
  <div>
    <FlowEyebrow tone={tone}>{eyebrow}</FlowEyebrow>
    <div style={{ marginTop: 10, fontSize: 14.5, lineHeight: 1.6, color: '#1f2937' }}>
      {children}
    </div>
  </div>
);

const SalesModuleBody: React.FC = () => (
  <Space direction="vertical" size={20} style={{ width: '100%' }}>
    {/* Этапы 1–4: сверху-вниз */}
    <div>
      <FlowEyebrow>Сверху-вниз · Этапы 1–4</FlowEyebrow>
      <ol
        style={{
          margin: '14px 0 0',
          padding: 0,
          listStyle: 'none',
          display: 'grid',
          gap: 10,
        }}
      >
        {SALES_TOP_DOWN_STAGES.map((label, idx) => (
          <li
            key={idx}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              fontSize: 14.5,
              lineHeight: 1.55,
              color: '#1f2937',
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 22,
                height: 22,
                flexShrink: 0,
                borderRadius: '50%',
                background: 'rgba(30, 64, 175, 0.10)',
                color: '#1e40af',
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {idx + 1}
            </span>
            <span>{label}</span>
          </li>
        ))}
      </ol>
      <div
        style={{
          marginTop: 14,
          paddingLeft: 12,
          borderLeft: '2px solid rgba(30, 64, 175, 0.18)',
          color: '#64748b',
          fontSize: 13.5,
          lineHeight: 1.55,
        }}
      >
        В том числе Валовая прибыль KPI и Мотивационный план продаж
      </div>
    </div>

    <FlowItem eyebrow="Снизу-вверх · Этап 5">
      План от МП в разрезе Клиент-ЦПР-ТН-SKU
    </FlowItem>

    <FlowItem eyebrow="Динамический план" tone="muted">
      План от МП в разрезе Клиент-ЦПР-ТН-SKU
    </FlowItem>
  </Space>
);

const PurchasesModuleBody: React.FC = () => (
  <Space direction="vertical" size={20} style={{ width: '100%' }}>
    <FlowItem eyebrow="Этап 1">
      План закупок в разрезе ЦПР-ТН-SKU
    </FlowItem>

    <FlowItem eyebrow="Динамический план" tone="muted">
      План закупок в разрезе ЦПР-ТН
    </FlowItem>
  </Space>
);

const LOGIC_MODULES: Array<{
  number: string;
  title: string;
  body: React.ReactNode;
  icon: React.ReactNode;
  color: string;
}> = [
  {
    number: '1',
    title: 'Модуль План продаж',
    icon: <RubleIcon style={{ fontSize: 40 }} />,
    color: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
    body: <SalesModuleBody />,
  },
  {
    number: '2',
    title: 'Модуль План закупок',
    icon: <ShoppingCartOutlined style={{ fontSize: 40 }} />,
    color: 'linear-gradient(135deg, #1e40af 0%, #60a5fa 100%)',
    body: <PurchasesModuleBody />,
  },
  {
    number: '3',
    title: 'Модуль Сверка',
    icon: <CheckCircleOutlined style={{ fontSize: 40 }} />,
    color: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
    body: (
      <Paragraph style={{ marginBottom: 0 }}>
        Сверка плана продаж и плана закупок в разрезе ЦПР-ТН-SKU
      </Paragraph>
    ),
  },
  {
    number: '4',
    title: 'Модуль Управления',
    icon: <ToolOutlined style={{ fontSize: 40 }} />,
    color: 'linear-gradient(135deg, #667eea 0%, #a78bfa 100%)',
    body: (
      <Paragraph style={{ marginBottom: 0 }}>
        Гибкая настройка параметров, участвующих в планировании: структура продаж и закупок, % отгрузки, корректировка цен и пр.
      </Paragraph>
    ),
  },
  {
    number: '5',
    title: 'Модуль Администрирования',
    icon: <SettingOutlined style={{ fontSize: 40 }} />,
    color: 'linear-gradient(135deg, #7c3aed 0%, #c084fc 100%)',
    body: (
      <Paragraph style={{ marginBottom: 0 }}>
        Гибкая настройка ролевой модели по этапам, объектам системы, Кастомизация оформления таблиц и названия столбцов и пр.
      </Paragraph>
    ),
  },
];

const FLEXIBILITY_FEATURES: Array<{
  number: number;
  title: string;
  body: React.ReactNode;
  icon: React.ReactNode;
}> = [
  {
    number: 1,
    title: 'Формирование ключевого ассортимента',
    icon: <DatabaseOutlined style={{ fontSize: 32 }} />,
    body: <>С автоматическим обновлением структуры во всех формах Планов продаж и закупок на всех этапах</>,
  },
  {
    number: 2,
    title: 'Гибкое управление признаками КА',
    icon: <StarOutlined style={{ fontSize: 32 }} />,
    body: <>Фокусные препараты / проектные</>,
  },
  {
    number: 3,
    title: 'Управление ценами продаж и закупок',
    icon: <RubleIcon style={{ fontSize: 32 }} />,
    body: (
      <>
        <Text strong>- Доступны к просмотру</Text> различные варианты цен: Регистрационная цена, Средняя цена по региону, Средняя цена по компании, Цена закупки
        <br />
        - Реализована <Text strong>возможность ручной корректировки цен</Text>
      </>
    ),
  },
  {
    number: 4,
    title: 'Гибкое управление структурой отгрузок по периодам',
    icon: <BarChartOutlined style={{ fontSize: 32 }} />,
    body: (
      <>
        - По статистике в разрезе БГ / компании
        <br />
        - Кастомное распределение объема отгрузки (ручной ввод)
      </>
    ),
  },
  {
    number: 5,
    title: 'Распределение поставщиков по ЦПР-ТН',
    icon: <ApartmentOutlined style={{ fontSize: 32 }} />,
    body: <>и модуль КНП1, КНП2, КНП3, КНП Об.</>,
  },
  {
    number: 6,
    title: 'Управление клиентами',
    icon: <TeamOutlined style={{ fontSize: 32 }} />,
    body: <>Возможность добавления одному партнеру несколько менеджеров</>,
  },
  {
    number: 7,
    title: 'Управление датой старта динамического бюджета',
    icon: <PieChartOutlined style={{ fontSize: 32 }} />,
    body: <>Может устанавливаться кастомно</>,
  },
];

const INTEGRATIONS: Array<{ title: string; body: React.ReactNode; icon: React.ReactNode }> = [
  {
    title: 'Получение данных из 1С',
    icon: <ApiOutlined style={{ fontSize: 32 }} />,
    body: <>SKU, партнеры, цены, факты отгрузок, остаток к отгрузке и пр.</>,
  },
  {
    title: 'Передача данных в PDCA+',
    icon: <CloudOutlined style={{ fontSize: 32 }} />,
    body: (
      <>
        В части структуры ключевого ассортимента, признаков фокусный / проектный, поставщиков по ЦПР-ТН, планов продаж и закупок годовых и динамических
      </>
    ),
  },
  {
    title: 'Передача данных в PowerBI',
    icon: <BarChartOutlined style={{ fontSize: 32 }} />,
    body: <>Валовая прибыль KPI и мотивационный план продаж</>,
  },
];

// ---------------------------------------------------------------------------
// Вспомогательные подкомпоненты — чтобы JSX основной страницы оставался
// плоским и читаемым. Стилей кастомных нет: только Ant Design + inline.
// ---------------------------------------------------------------------------

const SectionHeading: React.FC<{
  title: string;
  align?: 'center' | 'left';
}> = ({ title, align = 'center' }) => (
  <div
    style={{
      textAlign: align,
      marginBottom: 48,
      maxWidth: 880,
      ...(align === 'center' ? { marginInline: 'auto' } : {}),
    }}
  >
    <Title level={2} style={{ marginTop: 0, marginBottom: 0, color: '#0f172a' }}>
      {title}
    </Title>
  </div>
);

const DemoPage: React.FC = () => {
  const heroRef = useRef<HTMLElement>(null);
  const demoTableRef = useRef<HTMLDivElement>(null);
  const tour = useErgonomicsTour(demoTableRef);

  return (
    <div className={styles.longreadContainer}>
      <div className={styles.heroBackdrop}>
        <div className={styles.heroGradient} />
      </div>

      {/* HERO ----------------------------------------------------------- */}
      <section ref={heroRef} className={styles.heroSection}>
        <div className={styles.heroContent}>
          <Title level={1} className={styles.heroTitle}>
            PDCA+ S&OP
          </Title>
          <Paragraph className={styles.heroDescription}>
            Инструмент планирования продаж и закупок
          </Paragraph>
        </div>
      </section>

      {/* Прозрачность процесса и сохранность данных --------------------- */}
      <section className={styles.section}>
        <div className={styles.sectionContent}>
          <SectionHeading title="Прозрачность процесса и сохранность данных" />

          <Card
            style={{ borderRadius: 16 }}
            styles={{ body: { padding: '32px 36px' } }}
          >
            <Space direction="vertical" size={14} style={{ width: '100%' }}>
              {TRANSPARENCY_BULLETS.map((b, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 14,
                    fontSize: 16,
                    lineHeight: 1.6,
                    color: '#1f2937',
                  }}
                >
                  <CheckCircleOutlined
                    style={{ color: '#1e40af', fontSize: 20, marginTop: 2, flexShrink: 0 }}
                  />
                  <span>{b}</span>
                </div>
              ))}
            </Space>
          </Card>
        </div>
      </section>

      {/* 1. Общая логика инструмента ------------------------------------ */}
      <section className={`${styles.section} ${styles.sectionAlt}`}>
        <div className={styles.sectionContent}>
          <SectionHeading title="Общая логика инструмента" />

          <Title
            level={3}
            style={{ marginTop: 0, marginBottom: 24, color: '#1e3a8a', textAlign: 'center' }}
          >
            Логика выстроена на базе верхнеуровневого целевого БП
          </Title>

          <Row gutter={[32, 32]}>
            {LOGIC_MODULES.map((m, i) => (
              <Col xs={24} md={12} key={`${m.number}-${i}`}>
                <div
                  style={{
                    position: 'relative',
                    height: '100%',
                    background: '#fff',
                    padding: '40px 40px 36px',
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    borderRadius: 4,
                    transition: 'border-color 0.25s ease, transform 0.25s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(30, 64, 175, 0.35)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(15, 23, 42, 0.08)';
                    e.currentTarget.style.transform = '';
                  }}
                >
                  {/* Маленький цветной маркер + порядковый номер модуля */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      marginBottom: 18,
                      color: '#94a3b8',
                      fontSize: 12,
                      letterSpacing: '0.16em',
                      fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: 28,
                        height: 2,
                        background: m.color,
                      }}
                    />
                    <span>{String(m.number).padStart(2, '0')}</span>
                  </div>

                  <Title
                    level={3}
                    style={{
                      marginTop: 0,
                      marginBottom: 22,
                      color: '#0f172a',
                      fontWeight: 600,
                      fontSize: 24,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {m.title}
                  </Title>

                  <div
                    style={{
                      color: '#475569',
                      fontSize: 15,
                      lineHeight: 1.7,
                    }}
                  >
                    {m.body}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* 2. Гибкость управления ----------------------------------------- */}
      <section className={styles.section}>
        <div className={styles.sectionContent}>
          <SectionHeading title="Гибкость управления" />

          <Row gutter={[32, 32]}>
            {FLEXIBILITY_FEATURES.map((f) => (
              <Col xs={24} md={12} lg={8} key={f.number}>
                <div
                  style={{
                    position: 'relative',
                    height: '100%',
                    background: '#fff',
                    padding: '32px 32px 28px',
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    borderRadius: 4,
                    transition: 'border-color 0.25s ease, transform 0.25s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(30, 64, 175, 0.35)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(15, 23, 42, 0.08)';
                    e.currentTarget.style.transform = '';
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      marginBottom: 16,
                      color: '#94a3b8',
                      fontSize: 12,
                      letterSpacing: '0.16em',
                      fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: 22,
                        height: 2,
                        background: '#1e40af',
                      }}
                    />
                    <span>{String(f.number).padStart(2, '0')}</span>
                  </div>

                  <Title
                    level={4}
                    style={{
                      marginTop: 0,
                      marginBottom: 14,
                      color: '#0f172a',
                      fontWeight: 600,
                      fontSize: 18,
                      lineHeight: 1.35,
                      letterSpacing: '-0.005em',
                    }}
                  >
                    {f.title}
                  </Title>

                  <div
                    style={{
                      color: '#475569',
                      fontSize: 14.5,
                      lineHeight: 1.65,
                    }}
                  >
                    {f.body}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* 3. Эргономика --------------------------------------------------- */}
      <section className={`${styles.section} ${styles.sectionAlt}`}>
        <div className={styles.sectionContent}>
          <SectionHeading title="Эргономика" />

          <Row gutter={[24, 24]}>
            <Col xs={24} md={8}>
              <Card
                style={{ height: '100%', borderRadius: 16 }}
                styles={{ body: { padding: 28 } }}
              >
                <Title level={5} style={{ marginTop: 0, color: '#0f172a' }}>
                  Бесшовная взаимосвязь всех этапов
                </Title>
                <Paragraph style={{ marginBottom: 0, color: '#475569', fontSize: 15 }}>
                  без необходимости дублирования информации
                </Paragraph>
              </Card>
            </Col>

            <Col xs={24} md={8}>
              <Card
                style={{ height: '100%', borderRadius: 16 }}
                styles={{ body: { padding: 28 } }}
              >
                <Title level={5} style={{ marginTop: 0, color: '#0f172a' }}>
                  План продаж содержит максимальную информацию во всех разрезах с возможностью фильтрации:
                </Title>
                <Paragraph style={{ marginBottom: 0, color: '#475569', fontSize: 15 }}>
                  по клиентам, по остаткам к отгрузке (из ЗК 1С), по ЦПР, по ТН, по фактам и пр.
                </Paragraph>
              </Card>
            </Col>

            <Col xs={24} md={8}>
              <Card
                style={{ height: '100%', borderRadius: 16 }}
                styles={{ body: { padding: 28 } }}
              >
                <Title level={5} style={{ marginTop: 0, color: '#0f172a' }}>
                  Интерфейс с максимальной привычной эргономикой ЭКСЕЛЯ
                </Title>
                <Paragraph style={{ marginBottom: 0, color: '#475569', fontSize: 15 }}>
                  с дополнительными функциями он-лайн отображения данных из 1С, моментального расчета данных,
                  интерактивностью (цветовая индикация измененных ячеек),
                  автоматическим обновлением структуры ЦПР-ТН-SKU при актуализации и пр.
                </Paragraph>
              </Card>
            </Col>
          </Row>

          {/* Живой пример таблицы — динамический план продаж (моковые данные) */}
          <div ref={demoTableRef} style={{ marginTop: 56 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 14,
                color: '#94a3b8',
                fontSize: 12,
                letterSpacing: '0.16em',
                fontWeight: 600,
              }}
            >
              <span style={{ display: 'inline-block', width: 28, height: 2, background: '#1e40af' }} />
              <span>ЖИВОЙ ПРИМЕР</span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 16,
                marginBottom: 10,
                flexWrap: 'wrap',
              }}
            >
              <Title
                level={3}
                style={{
                  marginTop: 0,
                  marginBottom: 0,
                  color: '#0f172a',
                  fontWeight: 600,
                  fontSize: 24,
                  letterSpacing: '-0.01em',
                }}
              >
                Динамический план продаж
              </Title>
              <ErgonomicsTourButton onClick={tour.openTour} />
            </div>
            <Paragraph style={{ marginBottom: 24, color: '#475569', fontSize: 15, maxWidth: 760 }}>
              Поработайте с примером ниже, чтобы почувствовать эргономику. Данные моковые — изменения нигде не сохраняются.
            </Paragraph>

            <Card
              style={{ borderRadius: 16, overflow: 'hidden' }}
              styles={{ body: { padding: 16 } }}
            >
              <ErgonomicsDemoTable />
            </Card>

            <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
              {[
                {
                  title: 'Сворачивание групп колонок',
                  body: 'Кликните по стрелке у месяца в шапке — он свернётся до одной колонки. Месяцы можно показывать выборочно.',
                },
                {
                  title: 'Подсветка изменений',
                  body: 'Любая ваша правка подсвечивается жёлтым, чтобы было видно, что ещё не сохранено.',
                },
                {
                  title: 'Плавающий тулбар «Сохранить / Отменить»',
                  body: 'Как только появляется хотя бы одно изменение, снизу всплывает кнопка сохранения или отмены.',
                },
                {
                  title: 'Фильтры в шапке',
                  body: 'Кликните по треугольнику в заголовке колонки, чтобы отфильтровать значения.',
                },
                {
                  title: 'Цветовая индикация отклонений',
                  body: 'Колонка «Отклонение» подсвечивается красным или зелёным в зависимости от знака.',
                },
                {
                  title: 'Редактируемые и только для просмотра',
                  body: 'Дин.План и месячные планы можно править, факт и справочные поля — только смотреть.',
                },
                {
                  title: 'Числовые форматы',
                  body: 'Цены и объёмы показываются в локальном формате с разделителями тысяч.',
                },
                {
                  title: 'Пагинация и сортировка',
                  body: 'Меняйте число строк на странице, кликайте по заголовку для сортировки.',
                },
              ].map((f) => (
                <Col xs={24} sm={12} md={8} key={f.title}>
                  <div
                    style={{
                      padding: '14px 16px',
                      border: '1px solid rgba(15, 23, 42, 0.08)',
                      borderRadius: 8,
                      background: '#fff',
                      height: '100%',
                    }}
                  >
                    <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: 4, fontSize: 14.5 }}>
                      {f.title}
                    </div>
                    <div style={{ color: '#64748b', fontSize: 13.5, lineHeight: 1.55 }}>
                      {f.body}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </div>
      </section>

      <ErgonomicsTour open={tour.open} onClose={tour.closeTour} />

      {/* 4. Интеграция с 1С, PDCA+, PowerBI ------------------------------ */}
      <section className={styles.section}>
        <div className={styles.sectionContent}>
          <SectionHeading title="Интеграция с 1С, PDCA+, PowerBI" />

          <Row gutter={[24, 24]}>
            {INTEGRATIONS.map((it) => (
              <Col xs={24} md={8} key={it.title}>
                <Card
                  hoverable
                  style={{ height: '100%', borderRadius: 16 }}
                  styles={{ body: { padding: 28 } }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 14,
                      background: 'rgba(30, 64, 175, 0.08)',
                      color: '#1e40af',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 18,
                    }}
                  >
                    {it.icon}
                  </div>
                  <Title level={5} style={{ marginTop: 0, marginBottom: 8, color: '#0f172a' }}>
                    {it.title}
                  </Title>
                  <Paragraph style={{ marginBottom: 0, color: '#475569', fontSize: 15, lineHeight: 1.6 }}>
                    {it.body}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>
    </div>
  );
};

/* =============================================================================
 * Контент главной страницы — временно скрыт (в разработке)
 * ===========================================================================*/

/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/rules-of-hooks */
// @ts-expect-error — компонент временно скрыт (в разработке), используется как ссылка
const _DemoPageContent: React.FC = () => {
  const navigate = useNavigate();
  const modulesSectionRef = useRef<HTMLElement>(null);

  return (
    <div className={styles.longreadContainer}>
      <div className={styles.heroBackdrop}>
        <div className={styles.heroGradient} />
      </div>
      <section className={styles.heroSection}>
        <div className={styles.heroContent}>
          <Title level={1} className={styles.heroTitle}>
            PLNG-APP
          </Title>
          <Paragraph className={styles.heroDescription}>
            Интеллектуальная платформа для продвинутого планирования продаж и закупок
          </Paragraph>
          <Button
            type="primary"
            size="large"
            icon={<RocketOutlined />}
            className={styles.heroButton}
            onClick={() => {
              modulesSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Узнать больше
          </Button>
        </div>
        <div/>
      </section>

      <section ref={modulesSectionRef} id="modules-section" className={styles.section}>
        <div className={styles.sectionContent}>
          <SystemModules navigate={navigate} />
        </div>
      </section>

      <section className={`${styles.section} ${styles.sectionAlt}`}>
        <div className={styles.sectionContent}>
          <SalesPlanningStages />
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionContent}>
          <TechStack />
        </div>
      </section>

      <section className={`${styles.section} ${styles.sectionAlt}`}>
        <div className={styles.sectionContent}>
          <KeyFeatures />
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionContent}>
          <NavigationToModules />
        </div>
      </section>
    </div>
  );
};

const SystemModules: React.FC<{ navigate: NavigateFunction }> = ({ navigate }) => {
  const { elementRef: sectionRef, isVisible: sectionVisible } = useIntersectionObserver();
  
  const modules = [
    {
      key: 'sales',
      icon: <RubleIcon style={{ fontSize: 64 }} />,
      title: 'Планирование продаж',
      description: 'Cистема планирования от бизнеса-групп до менеджеров',
      features: ['Описание фьючи 1', 'Описание фьючи 2', 'Описание фьючи 3', 'Описание фьючи 4'],
      color: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
      path: '/sales',
      badge: '5 этапов',
    },
    {
      key: 'purchase',
      icon: <ShoppingCartOutlined style={{ fontSize: 64 }} />,
      title: 'Планирование закупок',
      description: 'Оптимизация закупок на основе планов продаж',
      features: ['Описание фьючи 1', 'Описание фьючи 2', 'Описание фьючи 3', 'Описание фьючи 4'],
      color: 'linear-gradient(135deg, #1e40af 0%, #60a5fa 100%)',
      path: '/purchase',
      badge: '2 этапа',
    },
    {
      key: 'references',
      icon: <DatabaseOutlined style={{ fontSize: 64 }} />,
      title: 'Управление',
      description: 'Управление ключевым ассортиментом и ценами',
      features: ['Описание фьючи 1', 'Описание фьючи 2', 'Описание фьючи 3', 'Описание фьючи 4'],
      color: 'linear-gradient(135deg, #667eea 0%, #a78bfa 100%)',
      path: '/references',
      badge: 'Административная панель',
    },
    {
      key: 'admin',
      icon: <SettingOutlined style={{ fontSize: 64 }} />,
      title: 'Администрирование',
      description: 'Настройки системы и управление доступом',
      features: ['Описание фьючи 1', 'Описание фьючи 2', 'Описание фьючи 3', 'Описание фьючи 4'],
      color: 'linear-gradient(135deg, #7c3aed 0%, #c084fc 100%)',
      path: '/admin',
      badge: 'Административная панель',
    },
  ];

  return (
    <div ref={sectionRef} className={`${styles.modulesSection} ${sectionVisible ? styles.scrollAnimateUp + ' ' + styles.visible : styles.scrollAnimateUp}`}>
      <div className={styles.sectionHeader}>
        <Title level={2}>Основные модули системы</Title>
        <Paragraph>
          PLNG-APP объединяет ключевые блоки управления продажами, закупками и справочниками в едином контуре
        </Paragraph>
      </div>

      <Row gutter={[24, 24]}>
        {modules.map((module) => (
          <Col xs={24} lg={12} key={module.key}>
            <Card
                className={styles.moduleCard}
              hoverable
              onClick={() => navigate(module.path)}
              style={{
                background: '#ffffff',
                minHeight: '320px',
                border: '1px solid rgba(148, 163, 184, 0.18)',
              }}
              styles={{ body: { padding: '32px' } }}
            >
              <div className={styles.moduleGradient} style={{ background: module.color }} />
              <div className={styles.moduleCardContent}>
                <div className={styles.moduleIcon}>{module.icon}</div>
                <div className={styles.moduleInfo}>
                  <div className={styles.moduleHeader}>
                    <div>
                      <Title level={3} className={styles.moduleTitle}>
                        {module.title}
                      </Title>
                      <Paragraph className={styles.moduleDescription}>{module.description}</Paragraph>
                    </div>
                    <Tag className={styles.moduleBadge}>{module.badge}</Tag>
                  </div>
                  <ul className={styles.moduleFeatureList}>
                    {module.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className={styles.moduleFeatureItem}>
                        <CheckCircleOutlined />
                        <span>{feature}</span>
                      </li>
                    ))}
                    </ul>
                  </div>
                </div>
              </Card>
            </Col>
        ))}
      </Row>
    </div>
  );
};

const SalesPlanningStages: React.FC = () => {
  const { elementRef: sectionRef, isVisible: sectionVisible } = useIntersectionObserver();
  const stages = [
    {
      stage: 1,
      title: 'План продаж по БГ',
      description: 'Планирование на уровне бизнес-групп',
      icon: <RubleIcon style={{ fontSize: 32 }} />,
      features: ['Описание фьючи 1', 'Описание фьючи 2', 'Описание фьючи 3', 'Описание фьючи 4'],
      status: 'completed',
    },
    {
      stage: 2,
      title: 'План продаж по ЦПР-ТН-SKU',
      description: 'Детализация по центрам прибыли и товарным номенклатурам',
      icon: <BarChartOutlined style={{ fontSize: 32 }} />,
      features: ['Описание фьючи 1', 'Описание фьючи 2', 'Описание фьючи 3', 'Описание фьючи 4'],
      status: 'completed',
    },
    {
      stage: 3,
      title: 'План закупок и продаж',
      description: 'Объединенное планирование закупок и сбыта',
      icon: <CheckCircleOutlined style={{ fontSize: 32 }} />,
      features: ['Описание фьючи 1', 'Описание фьючи 2', 'Описание фьючи 3', 'Описание фьючи 4'],
      status: 'development',
    },
    {
      stage: 4,
      title: 'План продажи БГ по МП',
      description: 'Распределение планов бизнес-групп по менеджерам продаж',
      icon: <ApartmentOutlined style={{ fontSize: 32 }} />,
      features: ['Описание фьючи 1', 'Описание фьючи 2', 'Описание фьючи 3', 'Описание фьючи 4'],
      status: 'development',
    },
    {
      stage: 5,
      title: 'План продажи по МП',
      description: 'Индивидуальное планирование на уровне менеджеров продаж',
      icon: <PieChartOutlined style={{ fontSize: 32 }} />,
      features: ['Описание фьючи 1', 'Описание фьючи 2', 'Описание фьючи 3', 'Описание фьючи 4'],
      status: 'development',
    },
  ];

  return (
    <div ref={sectionRef} className={`${styles.stagesSection} ${sectionVisible ? styles.scrollAnimateLeft + ' ' + styles.visible : styles.scrollAnimateLeft}`}>
      <div className={styles.sectionHeader}>
        <Title level={2}>5 этапов планирования продаж</Title>
        <Paragraph>Пошаговый процесс от стратегического планирования до индивидуальных целей менеджеров</Paragraph>
      </div>

      <Timeline
        mode="left"
        className={styles.stagesTimeline}
        items={stages.map((stage) => ({
          children: (
            <Card className={styles.stageCard} style={{ marginBottom: '24px' }}>
              <div className={styles.stageHeader}>
                <div>
                  <Title level={4} style={{ margin: 0 }}>
                    Этап {stage.stage}: {stage.title}
                  </Title>
                  <Paragraph style={{ margin: '8px 0', color: '#666' }}>{stage.description}</Paragraph>
                </div>
                <Badge
                  status={stage.status === 'completed' ? 'success' : 'processing'}
                  text={stage.status === 'completed' ? 'Реализовано' : 'В разработке'}
                />
              </div>

              <div className={styles.stageFeatures}>
                {stage.features.map((feature, index) => (
                  <div key={index} className={styles.stageFeatureChip}>
                    <CheckCircleOutlined />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </Card>
          ),
        }))}
      />

      <Card className={styles.stagesInfo}>
        <Row gutter={[24, 24]} align="middle">
          <Col span={12}>
            <Statistic title="Этапов реализовано" value={2} suffix="/ 5" styles={{ content: { color: '#1890ff'  } }} prefix={<TrophyOutlined />} />
          </Col>
          <Col span={12}>
            <Statistic title="Готовность системы" value={40} suffix="%" styles={{ content: { color: '#1890ff'  } }} prefix={<CheckCircleOutlined />} />
          </Col>
        </Row>

        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Paragraph type="secondary">
            📋 Этапы 1-2: Полностью реализованы и готовы к использованию
            <br />
            🚧 Этапы 3-5: В активной разработке
          </Paragraph>
        </div>
      </Card>
    </div>
  );
};

const TechStack: React.FC = () => {
  const { elementRef: sectionRef, isVisible: sectionVisible } = useIntersectionObserver();
  const technologies = [
    {
      category: 'Фронтенд',
      items: [
        { name: 'React 18', description: 'Современная библиотека для пользовательского интерфейса' },
        { name: 'TypeScript', description: 'Типизированный JavaScript для надежности' },
        { name: 'Ant Design', description: 'Enterprise-класс UI библиотека' },
        { name: 'Handsontable', description: 'Excel-подобные таблицы с высокими возможностями' },
        { name: 'React Query', description: 'Управление серверным состоянием и кешированием' },
      ],
    },
    {
      category: 'Бэкенд',
      items: [
        { name: 'Azure Functions', description: 'Безсерверные вычисления в облаке' },
        { name: 'Node.js', description: 'Надежная среда выполнения для серверной логики' },
        { name: 'PostgreSQL', description: 'Реляционная база данных корпоративной класса' },
      ],
    },
    {
      category: 'Инфраструктура',
      items: [
        { name: 'Azure Static Web Apps', description: 'Быстрый хостинг веб-приложений' },
        { name: 'Microsoft MSAL', description: 'Аутентификация через Azure Active Directory' },
        { name: 'Azure DevOps', description: 'Полный цикл CI/CD и управление проектами' },
      ],
    },
  ];

  return (
    <div ref={sectionRef} className={`${styles.techSection} ${sectionVisible ? styles.scrollAnimateRight + ' ' + styles.visible : styles.scrollAnimateRight}`}>
      <div className={styles.sectionHeader}>
        <Title level={2}>Технологический стек</Title>
        <Paragraph>Современные инструменты для стабильной, масштабируемой и безопасной работы системы</Paragraph>
      </div>

      {technologies.map((category, index) => (
        <Card
          key={index}
          className={styles.techCard}
          title={
            <div className={styles.techCategoryHeader}>
              <Title level={3} style={{ margin: 0 }}>
                {category.category}
              </Title>
            </div>
          }
        >
          <Row gutter={[16, 16]}>
            {category.items.map((tech, techIndex) => (
              <Col xs={24} sm={12} lg={8} key={techIndex}>
                <Card size="small" className={styles.techItem} hoverable>
                  <div className={styles.techItemContent}>
                    <Text strong>{tech.name}</Text>
                    <Paragraph type="secondary" style={{ margin: '4px 0 0', fontSize: '12px' }}>
                      {tech.description}
                    </Paragraph>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      ))}
    </div>
  );
};

const KeyFeatures: React.FC = () => {
  const { elementRef: sectionRef, isVisible: sectionVisible } = useIntersectionObserver();
  const features = [
    {
      icon: <TableOutlined style={{ fontSize: 48 }} />,
      title: 'Excel-подобные таблицы',
      description: 'Handsontable обеспечивает привычный интерфейс с копированием, вставкой, фильтрами и сортировкой',
      benefits: ['Быстрое обучение', 'Привычный интерфейс', 'Высокая производительность'],
    },
    {
      icon: <ApiOutlined style={{ fontSize: 48 }} />,
      title: 'Умное кеширование',
      description: 'React Query автоматически кеширует данные и оптимизирует обращения к серверу',
      benefits: ['Быстрая загрузка', 'Офлайн режим', 'Синхронизация данных'],
    },
    {
      icon: <SafetyCertificateOutlined style={{ fontSize: 48 }} />,
      title: 'Корпоративная безопасность',
      description: 'Интеграция с Azure AD обеспечивает безопасный доступ через корпоративные учетные записи',
      benefits: ['Single Sign-On', 'Управление ролями', 'Аудит доступа'],
    },
    {
      icon: <CloudOutlined style={{ fontSize: 48 }} />,
      title: 'Облачная архитектура',
      description: 'Azure Static Web Apps и Functions обеспечивают масштабируемость и надежность',
      benefits: ['Высокая доступность', 'Автомасштабирование', 'Backup данных'],
    },
    {
      icon: <BugOutlined style={{ fontSize: 48 }} />,
      title: 'Обработка ошибок',
      description: 'Продвинутая система обработки ошибок с понятными сообщениями для пользователей',
      benefits: ['Error Boundary', 'Логирование', 'Уведомления'],
    },
    {
      icon: <EyeOutlined style={{ fontSize: 48 }} />,
      title: 'Отслеживание изменений',
      description: 'Система фиксирует каждое изменение и подсказывает, где остались несохраненные данные',
      benefits: ['Контроль версий', 'Откат изменений', 'История правок'],
    },
  ];

  return (
    <div ref={sectionRef} className={`${styles.featuresSection} ${sectionVisible ? styles.scrollAnimateUp + ' ' + styles.visible : styles.scrollAnimateUp}`}>
      <div className={styles.sectionHeader}>
        <Title level={2}>Ключевые преимущества</Title>
        <Paragraph>Почему PLNG — современное решение для планирования в фармацевтической отрасли</Paragraph>
      </div>

      <Row gutter={[24, 24]}>
        {features.map((feature, index) => (
          <Col xs={24} md={12} lg={8} key={index}>
            <Card 
              className={styles.featureCard} 
              hoverable 
              style={{ 
                height: '100%',
              }}
            >
              <div className={styles.featureIcon}>{feature.icon}</div>
              <Title level={4}>{feature.title}</Title>
              <Paragraph style={{ marginBottom: 16 }}>{feature.description}</Paragraph>
              <div className={styles.featureBenefits}>
                {feature.benefits.map((benefit, idx) => (
                  <div key={idx} className={styles.featureBenefit}>
                    <StarOutlined />
                    <span>{benefit}</span>
                  </div>
                ))}
                </div>
              </Card>
            </Col>
        ))}
      </Row>
    </div>
  );
};
/* eslint-enable @typescript-eslint/no-unused-vars, react-hooks/rules-of-hooks */

const NavigationToModules: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, roles } = useAuth();
  const { elementRef: sectionRef, isVisible: sectionVisible } = useIntersectionObserver();

  const canAccess = useCallback(
    (requiresAuth: boolean, requiredRole?: string) => {
      if (!requiresAuth) return true;
      if (!isAuthenticated) return false;
      if (requiredRole && !roles.includes(requiredRole)) return false;
      return true;
    },
    [isAuthenticated, roles],
  );

  const modules = [
    {
      title: 'Планирование продаж',
      description: 'Запустите многоэтапный процесс планирования для бизнес-команд',
      path: '/sales',
      icon: <RubleIcon style={{ fontSize: 32 }} />,
      color: '#1890ff',
      requiresAuth: false,
    },
    {
      title: 'Планирование закупок',
      description: 'Синхронизируйте закупки с актуальными планами продаж',
      path: '/purchase',
      icon: <ShoppingCartOutlined style={{ fontSize: 32 }} />,
      color: '#52c41a',
      requiresAuth: false,
    },
    {
      title: 'Управление',
      description: 'Управляйте ассортиментом, ценами и фокусными препаратами',
      path: '/references',
      icon: <DatabaseOutlined style={{ fontSize: 32 }} />,
      color: '#722ed1',
      requiresAuth: true,
      requiredRole: 'role.admin',
    },
    {
      title: 'Администрирование',
      description: 'Настройте роли, права и ключевые параметры системы',
      path: '/admin',
      icon: <SettingOutlined style={{ fontSize: 32 }} />,
      color: '#fa541c',
      requiresAuth: true,
      requiredRole: 'role.admin',
    },
  ];

  return (
    <div ref={sectionRef} className={`${styles.navigationSection} ${sectionVisible ? styles.scrollAnimateScale + ' ' + styles.visible : styles.scrollAnimateScale}`}>
      <div className={styles.sectionHeader}>
        <Title level={2}>Начните работу прямо сейчас</Title>
        <Paragraph>Выберите модуль, чтобы глубже познакомиться с функционалом системы</Paragraph>
      </div>

      {isAuthenticated && user && (
        <Card style={{ marginBottom: 24, background: '#f6ffed', border: '1px solid #b7eb8f' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <UserOutlined style={{ color: '#52c41a', fontSize: 24 }} />
            <div>
              <Text strong>Добро пожаловать, {user.displayName}!</Text>
              <br />
            </div>
          </div>
        </Card>
      )}

      <Row gutter={[16, 16]}>
        {modules.map((module) => {
          const accessible = canAccess(module.requiresAuth, module.requiredRole);

          return (
            <Col xs={24} sm={12} key={module.title}>
              <Card
                className={styles.navigationCard}
                hoverable={accessible}
                style={{
                  opacity: accessible ? 1 : 0.6,
                  cursor: accessible ? 'pointer' : 'not-allowed',
                  borderColor: accessible ? module.color : '#d9d9d9',
                }}
                onClick={() => accessible && navigate(module.path)}
              >
                <div className={styles.navigationCardContent}>
                  <div className={styles.navigationIcon} style={{ color: accessible ? module.color : '#999' }}>
                    {module.icon}
                  </div>
                  <div className={styles.navigationInfo}>
                    <Title level={4} style={{ margin: 0, color: accessible ? 'inherit' : '#999' }}>
                      {module.title}
                    </Title>
                    <Paragraph style={{ margin: '8px 0', color: accessible ? '#666' : '#999' }}>
                      {module.description}
                    </Paragraph>
                    {!accessible && (
                      <Tag color="orange">
                        <LockOutlined /> Требуется {module.requiredRole ? 'роль администратора' : 'авторизация'}
                      </Tag>
                    )}
                  </div>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Divider />

      <div className={styles.navigationNote}>
        <Title level={4}>Нужна помощь?</Title>
        <Paragraph>
          Мы сопровождаем внедрение на всех этапах — от онбординга команд до настройки аналитики и отчетности.
        </Paragraph>
        <Space size="middle">
          <Button type="default" icon={<FileProtectOutlined />}>
            Документация
          </Button>
          <Button type="primary" ghost icon={<TeamOutlined />}>
            Связаться с нами
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default DemoPage;
